import React, { useMemo } from "react";
import { DayPicker, Matcher } from "react-day-picker";

import "react-day-picker/dist/style.css";
import styles from "./deliveryDate.module.css";
import { useAppContext } from "../../context/AppContext";
import classNames from "classnames";
import moment from "moment";

interface Props {
  setDate: any;
  currentDate: any;
  inline?: any;
  gifting?: boolean;
  disabled?: boolean;
  deliveryType?: null | "48h" | "4d";
}

const DeliveryDate: React.FC<Props> = ({
  setDate,
  currentDate,
  inline,
  gifting,
  disabled,
  deliveryType,
}) => {
  useMemo(() => {
    if (!deliveryType) {
      setDate(null);
    }
  }, [deliveryType, setDate]);

  const oneDayInAdvance = new Date();
  oneDayInAdvance.setDate(new Date().getDate() + 1);
  const { blockedDates } = useAppContext();

  const filterOutAllWeekdaysExceptFor = (daysOfTheWeek: number[]) => {
    /**
     * For filterOutAllWeekdaysExceptFor[] to ALLOW the days given in the array
     * Monday = 1, Tuesday = 2, Wednesday = 3, Thursday = 4, Friday = 5, Saturday = 6, Sunday = 7
     ** E.g. [1, 4] would ALLOW Monday and Thursday
     */

    const disabledDays = [];

    const currentDate = new Date();
    const thisYear = currentDate.getFullYear();
    const januaryFirst = new Date(thisYear, 0, 1);
    const decemberThirtyFirst = new Date(thisYear, 11, 31);

    for (
      let date = new Date(januaryFirst);
      date <= decemberThirtyFirst;
      date.setDate(date.getDate() + 1)
    ) {
      const dayOfWeek = date.getDay();

      if (!daysOfTheWeek.includes(dayOfWeek)) {
        // Tuesday = 2, Friday = 5
        disabledDays.push({ from: new Date(date), to: new Date(date) });
      }
    }

    return disabledDays;
  };

  const today = moment();

  const blockThreeDaysInAdvance = today.clone().add(2, "days");
  const blockTwoDaysInAdvance = today.clone().add(1, "days");
  const cutOffTime = today.clone().hour(11).minute(30);
  const weekendDate =
    today.format("dddd") === "Saturday" || today.format("dddd") === "Sunday";

  if (
    (today.isAfter(cutOffTime) && today.format("dddd") === "Friday") ||
    weekendDate
  ) {
    // On the weekend, only start delivering from Weds
    blockThreeDaysInAdvance.add(3, "days").day(2); // Block untill tuesday
  }

  const futureBlocked = new Date();
  futureBlocked.setDate(new Date().getDate() + 15);

  const disabledDays: Matcher[] = [
    {
      from: new Date(2000, 1, 1),
      to: blockTwoDaysInAdvance.toDate(), //sets next available date to 2 days after today
    },
    {
      from: new Date(2022, 6, 30),
      to: new Date(2022, 7, 6),
    },
    {
      from: new Date(2022, 7, 22),
      to: new Date(2022, 7, 26),
    },
    {
      from: futureBlocked, // Block out any dates post 2 weeks after ordrer
      to: new Date(2100, 7, 6),
    },
    ...blockedDates,
  ];

  const disabledDaysGifting: Matcher[] = [
    {
      from: new Date(2000, 1, 1),
      to: oneDayInAdvance, // sets next available date to 1 days after today
    },
  ];

  const disabledDays48h: Matcher[] = [
    {
      from: new Date(2000, 1, 1),
      to: blockTwoDaysInAdvance, //sets next available date to 2 days after today
    },
    {
      from: futureBlocked, // Block out any dates post 2 weeks after ordrer
      to: new Date(2100, 7, 6),
    },
    ...filterOutAllWeekdaysExceptFor([2, 5]),
    ...blockedDates,
  ];

  //if it's before 11:30am, the delivery date will be +2 days minimun
  // if it's after 11:30am, the delivery date will be +3 days minimum

  const disabledDays4d: Matcher[] = [
    {
      from: new Date(2000, 1, 1),
      to:
        today.isBefore(cutOffTime) && !weekendDate
          ? blockTwoDaysInAdvance.toDate()
          : blockThreeDaysInAdvance.toDate(), //sets next available date to 4 days after today
    },
    {
      from: futureBlocked, // Block out any dates post 2 dates after ordrer
      to: new Date(2100, 7, 6),
    },
    ...filterOutAllWeekdaysExceptFor([1, 2, 3, 4, 5, 6]),
    ...blockedDates,
  ];

  return (
    <div
      className={classNames(styles.container, {
        [styles.inline]: inline,
        [styles.disabled]: disabled,
        [styles.giftingContainer]: gifting,
      })}
    >
      <div className={styles.calendarContainer}>
        <div className={styles.subContainer}>
          <div className={styles.title}>
            {`Delivery ${!gifting ? "time /" : ""} date*`}
            <span className={styles.subtitle}>
              {`Please select your preferred delivery date ${
                !gifting
                  ? "and timeslot below, please get in touch if there isn’t a day that suits you."
                  : ""
              }`}
            </span>
          </div>

          <div className={styles.picker}>
            <>
              {!deliveryType && !gifting && (
                <div className={styles.pickerCover}>
                  Please enter your postcode
                </div>
              )}
              <DayPicker
                mode="single"
                modifiersClassNames={{ selected: styles.selected }}
                selected={currentDate}
                onSelect={setDate}
                disabled={
                  gifting
                    ? disabledDaysGifting
                    : deliveryType === "48h"
                    ? disabledDays48h
                    : disabledDays4d
                }
              />
            </>
          </div>
          {/* {!gifting && (
            <div className={styles.timePicker}>
              <TimePicker activeDate={currentDate} setTime={setTime} />
            </div>
          )} */}
        </div>
      </div>
    </div>
  );
};

export default DeliveryDate;
